import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import styles from './DataTable.module.css'
import type { Column, DataTableProps, SortDir } from '../types'

function getRowKey<T>(
  row: T,
  index: number,
  rowKey: DataTableProps<T>['rowKey'],
): string {
  if (typeof rowKey === 'function') return rowKey(row, index)
  const v = row[rowKey]
  return v == null ? String(index) : String(v)
}

function renderCell<T>(row: T, col: Column<T>): ReactNode {
  if (col.render) return col.render(row)
  if (col.accessor) {
    const v = row[col.accessor]
    return v == null ? '' : String(v)
  }
  return null
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  pageSize,
  emptyText = 'No data',
  className,
  summaryRow,
  onSortChange,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>(null)
  const [page, setPage] = useState(0)

  const onSortChangeRef = useRef(onSortChange)
  onSortChangeRef.current = onSortChange
  useEffect(() => {
    onSortChangeRef.current?.(sortKey, sortDir)
  }, [sortKey, sortDir])

  const totalPages = pageSize
    ? Math.max(1, Math.ceil(data.length / pageSize))
    : 1
  const safePage = Math.min(page, totalPages - 1)
  const pageRows = useMemo(() => {
    if (!pageSize) return data
    const start = safePage * pageSize
    return data.slice(start, start + pageSize)
  }, [data, pageSize, safePage])

  const handleHeaderClick = (col: Column<T>) => {
    if (!col.sortable) return
    if (sortKey !== col.key) {
      setSortKey(col.key)
      setSortDir('asc')
      return
    }
    if (sortDir === 'asc') setSortDir('desc')
    else if (sortDir === 'desc') {
      setSortKey(null)
      setSortDir(null)
    } else setSortDir('asc')
  }

  const indicator = (col: Column<T>) => {
    if (!col.sortable) return null
    const active = sortKey === col.key
    if (!active) return <span className={styles.sortIndicator}>⇅</span>
    return (
      <span className={styles.sortIndicator}>
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    )
  }

  return (
    <div className={`${styles.wrapper} ${className ?? ''}`}>
      <div className={styles.scroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((col) => {
                const sortableCls = col.sortable ? styles.sortable : ''
                return (
                  <th
                    key={col.key}
                    className={`${styles.headerCell} ${sortableCls}`}
                    onClick={() => handleHeaderClick(col)}
                  >
                    <span>{col.title}</span>
                    {indicator(col)}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 && !loading ? (
              <tr>
                <td className={styles.empty} colSpan={columns.length}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              pageRows.map((row, i) => (
                <tr
                  key={getRowKey(row, safePage * (pageSize ?? 0) + i, rowKey)}
                  className={i % 2 === 0 ? styles.evenRow : styles.oddRow}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={styles.bodyCell}>
                      {renderCell(row, col)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {summaryRow && (
            <tfoot>
              <tr className={styles.summaryRow}>
                {columns.map((col, i) => (
                  <td key={col.key} className={styles.summaryCell}>
                    {summaryRow[i]}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>
      {pageSize && data.length > pageSize && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={safePage === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            type="button"
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i).map((p) => (
            <button
              key={p}
              className={`${styles.pageBtn} ${p === safePage ? styles.pageBtnActive : ''}`}
              onClick={() => setPage(p)}
              type="button"
            >
              {p + 1}
            </button>
          ))}
          <button
            className={styles.pageBtn}
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            type="button"
          >
            ›
          </button>
        </div>
      )}
    </div>
  )
}
